import React, { useEffect, useState, useRef } from 'react';
import { Menu, Moon, Sun, Search, CornerDownLeft, Lock, Home } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { searchSections, highlightMatch, SearchResult } from '../../lib/search';
function Highlighted({
  text,
  query
}: {
  text: string;
  query: string;
}) {
  const parts = highlightMatch(text, query);
  return <>
      {parts.map((p, i) => typeof p === 'string' ? <span key={i} data-id="element-128">{p}</span> : <mark key={i} className="bg-primary/20 text-primary font-medium rounded px-0.5" data-id="element-129">
            {p.match}
          </mark>)}
    </>;
}
export function TopBar({
  onOpenMenu
}: {
  onOpenMenu?: () => void;
}) {
  const [isDark, setIsDark] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (document.documentElement.classList.contains('dark')) {
      setIsDark(true);
    }
  }, []);
  const toggleDark = () => {
    document.documentElement.classList.toggle('dark');
    setIsDark(!isDark);
  };
  const results: SearchResult[] = query.trim() ? searchSections(query, 8) : [];
  // Close dropdown on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);
  // Cmd/Ctrl + K to focus
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);
  const goToResult = (r: SearchResult) => {
    setOpen(false);
    setQuery('');
    navigate(`/section/${r.sectionId}#${r.subsectionId}`);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      goToResult(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };
  return <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-x-4 border-b bg-background/80 px-4 backdrop-blur-md sm:gap-x-6 sm:px-6 lg:px-8" data-id="element-130">
      <button type="button" onClick={onOpenMenu} className="-m-2.5 p-2.5 text-muted-foreground lg:hidden hover:text-foreground" data-id="element-131">
        <span className="sr-only" data-id="element-132">Open sidebar</span>
        <Menu className="h-6 w-6" aria-hidden="true" data-id="element-133" />
      </button>

      {/* Separator */}
      <div className="h-6 w-px bg-border lg:hidden" aria-hidden="true" data-id="element-134" />

      <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6" data-id="element-135">
        <div className="flex flex-1 items-center" data-id="element-136">
          <div ref={containerRef} className="relative w-full max-w-md" data-id="element-137">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" data-id="element-138" />
            <input ref={inputRef} type="text" value={query} onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }} onFocus={() => query && setOpen(true)} onKeyDown={handleKeyDown} placeholder="Search..." className="h-9 w-full rounded-md border bg-muted/50 pl-10 pr-3 sm:pr-12 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" data-id="element-139" />
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-0.5 rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground" data-id="element-140">
              ⌘K
            </kbd>

            {/* Results dropdown */}
            {open && query.trim() && <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-40 rounded-lg border bg-popover shadow-xl overflow-hidden" data-id="element-141">
                {results.length === 0 ? <div className="p-6 text-center" data-id="element-142">
                    <div className="text-sm font-medium text-foreground" data-id="element-143">
                      No results
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground" data-id="element-144">
                      Try different keywords like "dispatch", "pricing", or
                      "QuickBooks".
                    </div>
                  </div> : <>
                    <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-b bg-muted/30" data-id="element-145">
                      {results.length} result{results.length === 1 ? '' : 's'}
                    </div>
                    <ul className="max-h-[60vh] overflow-y-auto py-1" data-id="element-146">
                      {results.map((r, i) => {
                  const Icon = r.sectionIcon;
                  const isActive = i === activeIndex;
                  return <li key={`${r.sectionId}-${r.subsectionId}`} data-id="element-147">
                            <button type="button" onMouseEnter={() => setActiveIndex(i)} onClick={() => goToResult(r)} className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors ${isActive ? 'bg-primary/10' : 'hover:bg-muted/60'}`} data-id="element-148">
                              <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`} data-id="element-149">
                                <Icon className="h-4 w-4" data-id="element-150" />
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-151">
                                <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-152">
                                  <span data-id="element-153">
                                    Section {r.sectionNumber} · {r.sectionTitle}
                                  </span>
                                </div>
                                <div className="mt-0.5 text-sm font-semibold text-foreground truncate" data-id="element-154">
                                  <Highlighted text={r.subsectionTitle} query={query} data-id="element-155" />
                                </div>
                                <div className="mt-1 text-xs text-muted-foreground line-clamp-2" data-id="element-156">
                                  <Highlighted text={r.snippet} query={query} data-id="element-157" />
                                </div>
                              </div>
                              {isActive && <CornerDownLeft className="mt-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" data-id="element-158" />}
                            </button>
                          </li>;
                })}
                    </ul>
                    <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground" data-id="element-159">
                      <div className="flex items-center gap-3" data-id="element-160">
                        <span data-id="element-161">
                          <kbd className="rounded border bg-background px-1 py-0.5" data-id="element-162">
                            ↑↓
                          </kbd>{' '}
                          navigate
                        </span>
                        <span data-id="element-163">
                          <kbd className="rounded border bg-background px-1 py-0.5" data-id="element-164">
                            ↵
                          </kbd>{' '}
                          open
                        </span>
                        <span data-id="element-165">
                          <kbd className="rounded border bg-background px-1 py-0.5" data-id="element-166">
                            esc
                          </kbd>{' '}
                          close
                        </span>
                      </div>
                    </div>
                  </>}
              </div>}
          </div>
          <Link to="/" aria-label="Home" className="lg:hidden flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0" data-id="element-167">
            <Home className="h-5 w-5" data-id="element-168" />
          </Link>
        </div>
        <div className="flex items-center gap-x-3 lg:gap-x-4" data-id="element-169">
          <span className="hidden md:inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground" data-id="element-170">
            <Lock className="h-3 w-3" data-id="element-171" />
            Private &amp; Confidential
          </span>

          <button onClick={toggleDark} type="button" className="-m-2.5 p-2.5 text-muted-foreground hover:text-foreground transition-colors" data-id="element-172">
            <span className="sr-only" data-id="element-173">Toggle dark mode</span>
            {isDark ? <Sun className="h-5 w-5" data-id="element-174" /> : <Moon className="h-5 w-5" data-id="element-175" />}
          </button>
        </div>
      </div>
    </header>;
}